import { getFCP, getLCP, getFID, getCLS, getTTFB, Metric } from 'web-vitals';

interface AnalyticsEvent {
  category: string;
  action: string;
  label?: string;
  value?: number;
}

interface PerformanceMetrics {
  fcp: number; // First Contentful Paint
  lcp: number; // Largest Contentful Paint
  fid: number; // First Input Delay
  cls: number; // Cumulative Layout Shift
  ttfb: number; // Time to First Byte
}

class Analytics {
  private static instance: Analytics;
  private initialized: boolean = false;

  private constructor() {
    // Private constructor to enforce singleton
  }

  public static getInstance(): Analytics {
    if (!Analytics.instance) {
      Analytics.instance = new Analytics();
    }
    return Analytics.instance;
  }

  public init(measurementId: string): void {
    if (this.initialized) return;

    // Initialize Google Analytics
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];
    function gtag(...args: any[]) {
      window.dataLayer.push(args);
    }
    gtag('js', new Date());
    gtag('config', measurementId);

    this.initialized = true;
    this.initializePerformanceTracking();
  }

  public trackEvent({ category, action, label, value }: AnalyticsEvent): void {
    if (!this.initialized) {
      console.warn('Analytics not initialized');
      return;
    }

    if (window.gtag) {
      window.gtag('event', action, {
        event_category: category,
        event_label: label,
        value: value
      });
    }
  }

  public trackPageView(path: string): void {
    if (!this.initialized) {
      console.warn('Analytics not initialized');
      return;
    }

    if (window.gtag) {
      window.gtag('config', process.env.REACT_APP_GA_MEASUREMENT_ID, {
        page_path: path
      });
    }
  }

  private initializePerformanceTracking(): void {
    // Track Core Web Vitals
    getFCP((metric: Metric) => this.trackPerformanceMetric('FCP', metric.value));
    getLCP((metric: Metric) => this.trackPerformanceMetric('LCP', metric.value));
    getFID((metric: Metric) => this.trackPerformanceMetric('FID', metric.value));
    getCLS((metric: Metric) => this.trackPerformanceMetric('CLS', metric.value));
    getTTFB((metric: Metric) => this.trackPerformanceMetric('TTFB', metric.value));

    // Track custom performance metrics
    this.trackCustomPerformanceMetrics();
  }

  private trackPerformanceMetric(name: string, value: number): void {
    this.trackEvent({
      category: 'Performance',
      action: name,
      value: Math.round(value)
    });
  }

  private trackCustomPerformanceMetrics(): void {
    // Track page load time
    window.addEventListener('load', () => {
      const navigationTiming = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      if (navigationTiming) {
        this.trackEvent({
          category: 'Performance',
          action: 'PageLoadTime',
          value: Math.round(navigationTiming.loadEventEnd - navigationTiming.startTime)
        });
      }
    });

    // Track resource loading
    const observer = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        if (entry.entryType === 'resource') {
          const resourceEntry = entry as PerformanceResourceTiming;
          this.trackEvent({
            category: 'Performance',
            action: 'ResourceTiming',
            label: resourceEntry.name,
            value: Math.round(resourceEntry.duration)
          });
        }
      });
    });

    observer.observe({ entryTypes: ['resource'] });
  }
}

// Export singleton instance
export const analytics = Analytics.getInstance();

// Types for window object
declare global {
  interface Window {
    dataLayer: any[];
  }
} 